import React, { useState } from "react";
import type { ReactElement, ComponentProps, Dispatch } from "react";

import { Box, Text, Pressable, Tooltip, Spinner } from "native-base";
import { Platform } from "react-native";

import type { TeacherTimetableReducerValues } from "pianofunclub-shared/components/Registers/TeacherTimetable";
import type { LessonData } from "pianofunclub-shared/types";
import {
  getFullName,
  getInstrumentIcon,
  getLessonTypeIcon,
} from "pianofunclub-shared/utils/extractors";
import type { Action, State } from "pianofunclub-shared/utils/reducers";

type BoxProps = ComponentProps<typeof Box>;

interface Props extends BoxProps {
  dispatchState: Dispatch<Action<TeacherTimetableReducerValues>>;
  index: number;
  instrument: string | undefined | null;
  lessonData: LessonData;
  lessonTimestampDate: Date;
  lessonTimestampString: string;
  lessonType: string;
  pupilFirstName: string | undefined;
  pupilLastName: string | undefined;
  schoolAddress: string;
  state: State<TeacherTimetableReducerValues>;
}

const TimetableLesson = (props: Props): ReactElement | null => {
  const {
    dispatchState,
    index,
    instrument,
    lessonData,
    lessonTimestampDate,
    lessonTimestampString,
    lessonType,
    pupilFirstName,
    pupilLastName,
    schoolAddress,
    state,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  return (
    <Tooltip
      label={
        pupilFirstName || pupilLastName ? getFullName(pupilFirstName, pupilLastName) : "Free Slot"
      }
      placement="bottom"
      pointerEvents="none"
      textAlign="center"
    >
      <Pressable
        flex={1}
        flexDirection="column"
        height="100%"
        onPress={() => {
          if (Platform.OS === "web") {
            dispatchState({
              input: "timetableLessonModalInfo",
              value: {
                dayIndex: (lessonTimestampDate.getDay() + 6) % 7,
                lessonData: lessonData,
                lessonTimestampDate: lessonTimestampDate,
                lessonTimestampString: lessonTimestampString,
                lessonType: lessonType,
                schoolAddress: schoolAddress,
              },
            });
          } else {
            // use a loading indicator on the tablet app because modal
            // is slow to appear
            setIsLoading(true);
            setTimeout(() => {
              dispatchState({
                input: "timetableLessonModalInfo",
                value: {
                  dayIndex: (lessonTimestampDate.getDay() + 6) % 7,
                  lessonData: lessonData,
                  lessonTimestampDate: lessonTimestampDate,
                  lessonTimestampString: lessonTimestampString,
                  lessonType: lessonType,
                  schoolAddress: schoolAddress,
                },
              });
              setIsLoading(false);
            }, 1);
          }
        }}
      >
        <Box
          alignItems="center"
          bg={
            lessonType === "INDIVIDUAL"
              ? "primary.100"
              : lessonType === "PAIRED"
                ? "secondary.200"
                : "primary.200"
          }
          borderRadius="lg"
          flex={1}
          flexDirection="row"
          ml={index !== 0 ? "0" : undefined}
          mx={"1"}
          px="2"
        >
          <Text
            color={!pupilFirstName ? "surface.700" : undefined}
            flex={1}
            fontFamily="Poppins-Regular"
            isTruncated
            mr="1"
          >
            {pupilFirstName
              ? state.values.timetableView === "DAY"
                ? getFullName(pupilFirstName, pupilLastName)
                : pupilFirstName
              : "Free Slot"}
          </Text>
          {isLoading ? (
            <Spinner />
          ) : (
            <>
              {getLessonTypeIcon(lessonType, {
                color: !pupilFirstName ? "surface.700" : "coolGray.800",
                minWidth: "4",
                mr: "2",
                size: "3",
              })}
              {instrument
                ? getInstrumentIcon(instrument, {
                    color: !pupilFirstName ? "surface.700" : "coolGray.800",
                    size: 4,
                  })
                : null}
            </>
          )}
        </Box>
      </Pressable>
    </Tooltip>
  );
};

export default React.memo(TimetableLesson);
